/**
 * /settings/guardrails/:policyId/edit — edit a document's name and raw
 * markdown. Honest about what's stored: one text document. Rule-shaped
 * content renders back through the policy viewer the moment it's saved.
 */
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Banner, Button, Input, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import { CrumbHeader } from './CrumbHeader.js';
import { usePoliciesListLogic, useUpdatePolicyLogic } from './useGuardrailsLogic.js';
import { GUARDRAILS_PATH } from './shared.js';
import type { AssistantPolicy } from '../../lib/types.js';

const EditForm = ({
  tenantId,
  policy,
  onDone,
}: {
  tenantId: string;
  policy: AssistantPolicy;
  onDone: () => void;
}) => {
  const updateLogic = useUpdatePolicyLogic(tenantId);
  const [name, setName] = useState(policy.name);
  const [document, setDocument] = useState(policy.document);

  const canSubmit =
    name.trim().length > 0 && document.trim().length > 0 && !updateLogic.isSubmitting;

  const handleSave = async () => {
    if (!canSubmit) return;
    try {
      await updateLogic.update(policy.id, { name: name.trim(), document });
      onDone();
    } catch {
      // Surfaced via updateLogic.error.
    }
  };

  return (
    <>
      {updateLogic.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{updateLogic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      <div className="mb-4 w-full max-w-[340px]">
        <label htmlFor="edit-name" className="text-ink mb-1.5 block text-xs font-semibold">
          Name
        </label>
        <Input
          id="edit-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={updateLogic.isSubmitting}
          autoComplete="off"
        />
      </div>

      <label htmlFor="edit-document" className="text-ink mb-1.5 block text-xs font-semibold">
        Document
      </label>
      <textarea
        id="edit-document"
        value={document}
        onChange={(e) => setDocument(e.target.value)}
        disabled={updateLogic.isSubmitting}
        rows={22}
        spellCheck={false}
        className="border-outline-strong bg-surface text-ink focus-visible:ring-primary w-full rounded-lg border p-4 font-mono text-[13px] leading-relaxed focus-visible:outline-none focus-visible:ring-2"
      />

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone} disabled={updateLogic.isSubmitting}>
          Cancel
        </Button>
        <Button onClick={() => void handleSave()} disabled={!canSubmit}>
          {updateLogic.isSubmitting ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </>
  );
};

export const PolicyEditPage = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const policiesQuery = usePoliciesListLogic(tenantId);

  if (meQuery.isLoading || !tenantId || policiesQuery.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-6 h-5 w-64" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </AppShell>
    );
  }

  const policy = (policiesQuery.data ?? []).find((p) => p.id === policyId);
  if (!policy) return <Navigate to={GUARDRAILS_PATH} replace />;

  const backToDocument = () => navigate(`${GUARDRAILS_PATH}/${encodeURIComponent(policy.id)}`);

  return (
    <AppShell>
      <CrumbHeader title={`Edit ${policy.name}`} onBack={backToDocument} />
      <h2 className="text-ink mb-4 text-2xl font-bold">Edit {policy.name}</h2>
      {/* key: a background refetch must not clobber in-progress typing */}
      <EditForm key={policy.id} tenantId={tenantId} policy={policy} onDone={backToDocument} />
    </AppShell>
  );
};
